import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import KgVis from "../../components/KgVisV2";
import AssociationTable from "../../components/AssociationTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Container, Row, Col, Alert, Button } from "react-bootstrap";
import { beApiUrl } from "../../api/be_config";
import _ from "lodash";
import './Graphshow.css'; // Import custom styles

export default function Graphshow() {
  const visNetworkRef = useRef(null);
  const twoHopVisNetworkRef = useRef(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryData = {
    entity_one_name: queryParams.get("nodeonename"),
    entity_one_type: queryParams.get("nodeonetype"),
    entity_two_name: queryParams.get("nodetwoname"),
    entity_two_type: queryParams.get("nodetwotype"),
    relation: queryParams.get("relationtype"),
    db_identifier: queryParams.get("db_identifier"),
    size_limit: 100,
  };


  const [edges, setEdges] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [triplets, setTriplets] = useState([]);
  const [rdyRender, setRdyRender] = useState(false);
  const [isLoadingMainGraph, setIsLoadingMainGraph] = useState(true);

  const [selectedNode, setSelectedNode] = useState(null);
  const [subgraph, setSubgraph] = useState({ nodes: [], edges: [], triplets: [] });
  const [twoHopPaths, setTwoHopPaths] = useState([]);
  const [isLoadingPaths, setIsLoadingPaths] = useState(false);
  const [entityOneId, setEntityOneId] = useState(null);
  const [pathCount, setPathCount] = useState(0);
  const [pathError, setPathError] = useState(null);
  const [twoHopGraph, setTwoHopGraph] = useState({ nodes: [], edges: [] });

  const handleDownloadTriplets = (data, filename = 'triplets.csv') => {
    if (!data || data.length === 0) {
      alert("No data to download.");
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => 
          `"${String(row[header]).replace(/"/g, '""')}"`
        ).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadTwoHopGraph = () => {
    if (twoHopVisNetworkRef.current) {
      const network = twoHopVisNetworkRef.current.getNetwork();
      if (network) {
        const canvas = network.canvas.frame.canvas;
        const dataURL = canvas.toDataURL("image/png");
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `two-hop-path_${selectedNode.label}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleDownloadSVG = () => {
    if (visNetworkRef.current) {
      const network = visNetworkRef.current.getNetwork();
      if (network) {
        // Vis-network does not have a direct SVG export. We get the canvas data URL.
        const canvas = network.canvas.frame.canvas;
        const dataURL = canvas.toDataURL("image/png");
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = 'knowledge-graph.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  // Calculate in-degrees for each node
  const calculateInDegrees = (edges) => {
    const inDegrees = {};
    edges.forEach(edge => {
      if (!inDegrees[edge.target]) {
        inDegrees[edge.target] = 0;
      }
      inDegrees[edge.target]++;
    });
    return inDegrees;
  };

  // Fetch data from the backend
  useEffect(() => {
    setIsLoadingMainGraph(true);
    fetch(beApiUrl + "kgpage/kgquery/v1/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(queryData),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to get the relations.");
        return response.json();
      })
      .then((data) => {
        if (!_.isEmpty(data) && data.triplets) {
            // Sort triplets by score and take the top 100
            const sortedTriplets = [...data.triplets].sort((a, b) => b.score - a.score);
            const topTriplets = sortedTriplets.slice(0, 100);

            // Reconstruct nodes and edges from the top 100 triplets
            const newNodes = new Map();
            const newEdges = [];

            topTriplets.forEach(triplet => {
              // Add nodes, ensuring no duplicates
              if (!newNodes.has(triplet.entity_one_id)) {
                newNodes.set(triplet.entity_one_id, { 
                  id: triplet.entity_one_id, 
                  label: triplet.entity_one_name, 
                  type: triplet.entity_one_type 
                });
              }
              if (!newNodes.has(triplet.entity_two_id)) {
                newNodes.set(triplet.entity_two_id, { 
                  id: triplet.entity_two_id, 
                  label: triplet.entity_two_name, 
                  type: triplet.entity_two_type 
                });
              }

              // Add edge for the triplet
              newEdges.push({
                id: `${triplet.entity_one_id}-${triplet.entity_two_id}-${triplet.relation}`,
                source: triplet.entity_one_id,
                target: triplet.entity_two_id,
                label: triplet.relation
              });
            });

            const finalNodes = Array.from(newNodes.values());
            const inDegrees = calculateInDegrees(newEdges);
            const updatedEdges = newEdges.map(edge => ({
              ...edge,
              width: Math.min(5, Math.max(1, inDegrees[edge.target] || 1)),
            }));

            setNodes(finalNodes);
            setEdges(updatedEdges);
            setTriplets(topTriplets);
          
          // Store entity_one_id for later use in two-hop paths query
          if (data.nodes.length > 0) {
            const entityOne = data.nodes.find(node => node.label === queryData.entity_one_name);
            if (entityOne) {
              setEntityOneId(entityOne.id);
            }
          }
          
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsLoadingMainGraph(false);
      });
  }, []);
  
  // Check if data is ready to render
  useEffect(() => {
    if (nodes.length > 0 && edges.length > 0) setRdyRender(true);
  }, [nodes, edges]);

  // Handle node click
  const handleNodeClick = (node) => {
    setSelectedNode(node);
    calculateSubgraph(node);
    fetchTwoHopPaths(node);
  };

  // Fetch two-hop paths between entity_one and the selected node
  const fetchTwoHopPaths = (targetNode) => {
    if (!entityOneId || !targetNode) return;
    
    setIsLoadingPaths(true);
    setTwoHopPaths([]);
    setPathCount(0);
    setPathError(null);
    setTwoHopGraph({ nodes: [], edges: [] });
    
    let targetTriplet = triplets.filter(item => item.entity_one_name === targetNode.label || item.entity_two_name === targetNode.label);
    targetTriplet = targetTriplet[0];
    const pathQueryData = {
      entity_one_id: targetTriplet.entity_one_id,
      entity_two_id: targetTriplet.entity_two_id,
      size_limit: 100,
      db_identifier: queryData.db_identifier ? parseInt(queryData.db_identifier) : 0
    };
    
    
    fetch(beApiUrl + "kgpage/two_hop_paths/v1/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(pathQueryData),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to get two-hop paths.");
        return response.json();
      })
      .then((data) => {
        if (data && data.paths) {
          setTwoHopPaths(data.paths);
          setPathCount(data.path_count || data.paths.length);
          
          // 使用返回的nodes和edges数据绘制子图
          if (data.nodes && data.edges) {
            setTwoHopGraph({
              nodes: data.nodes,
              edges: data.edges
            });
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching two-hop paths:", error);
        setPathError("Failed to fetch paths. Please try again.");
      })
      .finally(() => {
        setIsLoadingPaths(false);
      });
  };

  // Calculate subgraph based on selected node label
  const calculateSubgraph = (node) => {
    const relatedEdges = edges.filter(
      (edge) => edge.source === node.id || edge.target === node.id
    );

    const relatedNodeIds = new Set(
      relatedEdges.flatMap((edge) => [edge.source, edge.target])
    );
    
    const relatedNodes = nodes.filter((node) => relatedNodeIds.has(node.id));

    // Filter triplets based on node.label instead of ID
    const relatedTriplets = triplets.filter((triplet) => {
      return (
        triplet.entity_one_name === node.label || triplet.entity_two_name === node.label
      );
    });

    setSubgraph({ nodes: relatedNodes, edges: relatedEdges, triplets: relatedTriplets });
  };
  
  return (
    <Container>
      <Row>
        <Col xs={8}>
          <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Knowledge Graph</h5>
              <Button variant="primary" size="sm" onClick={handleDownloadSVG}>Download as PNG</Button>
            </div>
            <div className="main-graph-container">
            {isLoadingMainGraph ? (
              <LoadingSpinner 
                size="xl"
                variant="primary"
                title="Loading Knowledge Graph"
                subtitle="Please wait while we fetch the data"
                minHeight="500px"
              />
            ) : rdyRender ? (
              <KgVis ref={visNetworkRef} nodes={nodes} edges={edges} onNodeClick={handleNodeClick} />
            ) : (
              <Alert variant="warning" className="no-data-alert">
                No data available to display the knowledge graph.
              </Alert>
            )}
          </div>
        </Col>
        <Col xs={4}>
          <div className="right-panel">
            
            {selectedNode ? (
              <Alert variant="info" className="subgraph-alert">
                <h5 className="subgraph-title">
                  Subgraph: {selectedNode.label} ({selectedNode.id})
                </h5>

                {/* Scrollable Triplet List */}
                <div className="subgraph-triplets">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="section-title mb-0">Related Triplets</h6>
                    <Button 
                      variant="info" 
                      size="sm" 
                      onClick={() => handleDownloadTriplets(subgraph.triplets, `triplets_${selectedNode.label}.csv`)}>
                      Download Details
                    </Button>
                  </div>
                  <div className="triplet-list-container">
                    <Col>
                      {rdyRender && <AssociationTable triplets={subgraph.triplets} />}
                    </Col>
                  </div>
                </div>

                {/* Two-hop path visualization with loading state */}
                <div className="two-hop-visualization">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="section-title mb-0">Two-hop Path Visualization</h6>
                    {twoHopGraph.nodes.length > 0 && (
                      <Button 
                        variant="info" 
                        size="sm" 
                        onClick={handleDownloadTwoHopGraph}>
                        Download PNG
                      </Button>
                    )}
                  </div>
                  <div className="two-hop-graph-container">
                    {isLoadingPaths ? (
                      <LoadingSpinner 
                        size="md"
                        variant="info"
                        title="Loading Two-hop Paths"
                        minHeight="200px"
                        className="subgraph-loading"
                      />
                    ) : twoHopGraph.nodes.length > 0 ? (
                      <KgVis ref={twoHopVisNetworkRef} nodes={twoHopGraph.nodes} edges={twoHopGraph.edges} />
                    ) : (
                      <div className="no-subgraph-data">
                        <small className="text-muted">No two-hop path data available</small>
                      </div>
                    )}
                  </div>
                </div>
                
              </Alert>
            ) : (
              <div className="placeholder-panel">
                <div className="placeholder-content">
                  <h6>Node Selection</h6>
                  <p>Click on a node to view its connected subgraph and two-hop path visualization.</p>
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Triplet Data</h6>
            <Button variant="success" size="sm" onClick={() => handleDownloadTriplets(triplets)}>
              Download Triplets
            </Button>
          </div>
          {rdyRender && <AssociationTable triplets={triplets} />}
        </Col>
      </Row>
    </Container>
  );
}
